// Mis perfumes (22/9). Vive dentro de Mi armario: es tu inventario y
// vive con el inventario (decisión del dueño). La barra de arriba no
// crece: siete pestañas es lo que entra en un celular.
//
// QUÉ HACE Y QUÉ NO
//
//   · Cargás un perfume con su nombre. «Completar con IA» propone marca,
//     concentración, familia olfativa y momento del día; lo propuesto se
//     marca en ámbar hasta que lo mires, igual que en la carga de
//     prendas. La IA contesta desde lo que sabe, no busca en internet
//     (ver lib/perfumes.js): si no conoce el perfume lo dice y no
//     completa nada.
//   · La foto es la tuya, del frasco. No se bajan fotos de internet: son
//     de las marcas (decisión del dueño, 22/9).
//   · El nivel se actualiza a ojo con el deslizador, cuando quieras. Con
//     poco, «Qué comprar» avisa que se te está terminando.
//   · Con fecha de apertura, avisa a los tres años (puede empezar a
//     cambiar) y a los cinco (probablemente ya cambió).
//   · El uso sale de los registros: el frasco que elegís al tocar
//     «Me lo puse». Acá solo se lee.
//
// El orden es el de toda la app: primero lo que hace más tiempo que no
// usás. Un perfume que nunca usaste va arriba de todo.

import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { prepararFoto } from "./imagen";
import { haceCuanto, hoyLocal } from "./fechas";
import { fotoChica, fotoGrande } from "./foto";
import Visor from "./Visor";
import { t, tError, tPlural, useIdioma, idiomaActual, numero } from "./i18n";
import { IconoFrasco } from "./Iconos";
import {
  CONCENTRACIONES,
  FAMILIAS,
  MOMENTOS,
  nombreConcentracion,
  nombreFamilia,
  nombreMomento,
  seTermina,
  avisoApertura,
} from "./perfumesDatos";

/* Nunca usado primero; después, del uso más viejo al más nuevo. */
function ordenar(perfumes) {
  return [...perfumes].sort((a, b) => {
    if (!a.ultimo_uso && !b.ultimo_uso) return (b.creado_en || "").localeCompare(a.creado_en || "");
    if (!a.ultimo_uso) return -1;
    if (!b.ultimo_uso) return 1;
    return new Date(a.ultimo_uso) - new Date(b.ultimo_uso);
  });
}

export default function Perfumes({ recargarToken }) {
  useIdioma();
  const [perfumes, setPerfumes] = useState(null);
  const [error, setError] = useState("");
  const [editando, setEditando] = useState(null); // null | "nuevo" | perfume
  const [borrado, setBorrado] = useState(null);
  const [ampliada, setAmpliada] = useState(null);

  const traer = useCallback(async () => {
    try {
      setError("");
      const d = await api.listarPerfumes();
      setPerfumes(d.perfumes || []);
    } catch (e) {
      setError(tError(e));
      setPerfumes((p) => p || []);
    }
  }, []);

  useEffect(() => {
    traer();
  }, [traer, recargarToken]);

  const reemplazar = (p) =>
    setPerfumes((lista) => {
      const sin = (lista || []).filter((x) => x.id !== p.id);
      return p.activo === false ? sin : [p, ...sin];
    });

  async function borrar(p) {
    // Optimista, con «Deshacer»: la misma barra que el armario.
    const antes = perfumes;
    setPerfumes((l) => l.filter((x) => x.id !== p.id));
    setBorrado(p);
    try {
      await api.borrarPerfume(p.id);
    } catch (e) {
      setError(tError(e));
      setPerfumes(antes);
      setBorrado(null);
    }
  }

  async function deshacer() {
    const p = borrado;
    setBorrado(null);
    try {
      const r = await api.editarPerfume({ id: p.id, activo: true });
      reemplazar(r.perfume);
    } catch (e) {
      setError(tError(e));
    }
  }

  if (perfumes === null) return <p className="tenue">{t("perfumes.cargando", "Cargando tus perfumes...")}</p>;

  return (
    <section>
      {borrado && (
        <div className="barra-deshacer" role="status">
          <span>{t("perfumes.borrado", "Borraste {nombre}.", { nombre: borrado.nombre })}</span>
          <button type="button" className="boton-texto" onClick={deshacer}>
            {t("perfumes.deshacer", "Deshacer")}
          </button>
        </div>
      )}

      {error && (
        <div className="mensaje-error" role="alert">
          {error}
        </div>
      )}

      {editando ? (
        <FormPerfume
          inicial={editando === "nuevo" ? null : editando}
          onGuardado={(p) => {
            reemplazar(p);
            setEditando(null);
          }}
          onCancelar={() => setEditando(null)}
        />
      ) : (
        <button type="button" className="boton-primario perfumes-agregar" onClick={() => setEditando("nuevo")}>
          {t("perfumes.agregar", "Agregar perfume")}
        </button>
      )}

      {perfumes.length === 0 && !editando && (
        <p className="tenue">
          {t(
            "perfumes.vacio",
            "Todavía no cargaste ningún perfume. Agrega el primero con su nombre: la IA propone la marca, la concentración y la familia, y tú confirmas."
          )}
        </p>
      )}

      {perfumes.length > 0 && (
        <div className="grilla-prendas">
          {ordenar(perfumes).map((p) => (
            <TarjetaPerfume
              key={p.id}
              perfume={p}
              onCambio={reemplazar}
              onEditar={() => setEditando(p)}
              onBorrar={() => borrar(p)}
              onAmpliar={() => fotoGrande(p) && setAmpliada({ url: fotoGrande(p), alt: p.nombre })}
            />
          ))}
        </div>
      )}

      <Visor url={ampliada?.url} alt={ampliada?.alt} onCerrar={() => setAmpliada(null)} />
    </section>
  );
}

/* ------------------------------------------------------------------
   Una tarjeta
------------------------------------------------------------------ */

function TarjetaPerfume({ perfume: p, onCambio, onEditar, onBorrar, onAmpliar }) {
  useIdioma();
  const inputRef = useRef(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState("");
  const [nivel, setNivel] = useState(p.nivel ?? 100);
  const reloj = useRef(null);

  useEffect(() => setNivel(p.nivel ?? 100), [p.nivel]);
  useEffect(() => () => clearTimeout(reloj.current), []);

  /* El deslizador guarda solo cuando dejás de moverlo: sin la espera,
   * arrastrar de 80 a 40 serían cuarenta pedidos. */
  function moverNivel(v) {
    setNivel(v);
    clearTimeout(reloj.current);
    reloj.current = setTimeout(async () => {
      try {
        const r = await api.editarPerfume({ id: p.id, nivel: v });
        onCambio(r.perfume);
      } catch (e) {
        setError(tError(e));
        setNivel(p.nivel ?? 100);
      }
    }, 600);
  }

  async function cambiarFoto(archivo) {
    if (!archivo) return;
    setSubiendo(true);
    setError("");
    try {
      const foto = await prepararFoto(archivo);
      const r = await api.subirFotoPerfume({ id: p.id, ...foto });
      onCambio(r.perfume);
    } catch (e) {
      setError(tError(e));
    } finally {
      setSubiendo(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const aviso = avisoApertura(p);
  const datos = [nombreConcentracion(p.concentracion), nombreFamilia(p.familia), nombreMomento(p.momento)].filter(Boolean);

  return (
    <article className="prenda perfume">
      <div className="prenda-foto-caja">
        {fotoChica(p) ? (
          <img
            loading="lazy"
            decoding="async"
            src={fotoChica(p)}
            alt={p.nombre}
            className="prenda-foto"
            onClick={onAmpliar}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.currentTarget.click();
              }
            }}
          />
        ) : (
          <div className="prenda-foto prenda-sin-foto perfume-sin-foto">
            <IconoFrasco tamano={40} />
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => cambiarFoto(e.target.files?.[0])}
          style={{ display: "none" }}
        />
        <button
          type="button"
          className="boton-sobre-foto"
          onClick={() => inputRef.current?.click()}
          disabled={subiendo}
        >
          {subiendo
            ? t("perfumes.subiendo", "Subiendo...")
            : fotoChica(p)
              ? t("perfumes.cambiarFoto", "Cambiar foto")
              : t("perfumes.ponerFoto", "Poner foto")}
        </button>
      </div>

      <div className="prenda-cuerpo">
        <p className="prenda-nombre">{p.nombre}</p>
        {p.marca && <p className="texto-mini prenda-marca">{p.marca}</p>}
        {datos.length > 0 && <p className="texto-mini tenue perfume-datos">{datos.join(" · ")}</p>}
        {error && <p className="mensaje-error texto-mini" role="alert">{error}</p>}

        <p className="texto-mini prenda-uso">
          {p.ultimo_uso
            ? haceCuanto(p.ultimo_uso, { idioma: idiomaActual() })
            : t("perfumes.sinUsar", "Sin usar todavía")}
          {p.veces_usado > 0 && (
            <span className="tenue"> · {tPlural("plural.veces", p.veces_usado)}</span>
          )}
        </p>

        <label className="perfume-nivel">
          <span className="texto-mini">
            {t("perfumes.nivel", "Queda: {n}%", { n: nivel })}
            {p.ml ? <span className="tenue"> · {numero(p.ml)} ml</span> : null}
          </span>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={nivel}
            onChange={(e) => moverNivel(Number(e.target.value))}
            aria-label={t("perfumes.nivelEtiqueta", "Cuánto queda en el frasco")}
          />
        </label>

        {seTermina({ nivel }) && (
          <p className="aviso-dudosa texto-mini">{t("perfumes.seTermina", "Se te está terminando.")}</p>
        )}
        {aviso === "cinco" && (
          <p className="aviso-dudosa texto-mini">
            {t("perfumes.avisoCinco", "Abierto hace más de cinco años: probablemente ya cambió de olor.")}
          </p>
        )}
        {aviso === "tres" && (
          <p className="aviso-dudosa texto-mini">
            {t("perfumes.avisoTres", "Abierto hace más de tres años: puede empezar a degradarse.")}
          </p>
        )}

        <div className="prenda-acciones">
          <button type="button" className="boton-secundario texto-mini" onClick={onEditar}>
            {t("perfumes.editar", "Editar")}
          </button>
          <button type="button" className="boton-texto texto-mini boton-peligro" onClick={onBorrar}>
            {t("perfumes.borrar", "Borrar")}
          </button>
        </div>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------
   El formulario: alta y edición
------------------------------------------------------------------ */

const VACIO = {
  nombre: "",
  marca: "",
  concentracion: "",
  familia: "",
  momento: "",
  ml: "",
  nivel: 100,
  abierto_en: "",
};

function FormPerfume({ inicial, onGuardado, onCancelar }) {
  useIdioma();
  const [f, setF] = useState(() =>
    inicial
      ? {
          nombre: inicial.nombre || "",
          marca: inicial.marca || "",
          concentracion: inicial.concentracion || "",
          familia: inicial.familia || "",
          momento: inicial.momento || "",
          ml: inicial.ml ?? "",
          nivel: inicial.nivel ?? 100,
          abierto_en: inicial.abierto_en || "",
        }
      : VACIO
  );
  // Los campos que completó la IA y todavía nadie tocó: se ven en ámbar.
  const [adivinados, setAdivinados] = useState(new Set());
  const [consultando, setConsultando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const poner = (campo, valor) => {
    setF((x) => ({ ...x, [campo]: valor }));
    setAdivinados((s) => {
      if (!s.has(campo)) return s;
      const n = new Set(s);
      n.delete(campo);
      return n;
    });
  };
  const marca = (campo) => (adivinados.has(campo) ? "campo-adivinado" : "");

  async function completarConIA() {
    if (!f.nombre.trim()) {
      setError(t("perfumes.faltaNombre", "Escribe primero el nombre."));
      return;
    }
    setConsultando(true);
    setError("");
    setAviso("");
    try {
      const { propuesta } = await api.datosDePerfume({ nombre: f.nombre, marca: f.marca });
      if (!propuesta?.conocido) {
        setAviso(t("perfumes.iaNoConoce", "La IA no reconoce este perfume con seguridad. Completa los datos a mano."));
        return;
      }
      /* Solo se completa lo que está vacío. Lo que escribiste vos no lo
       * pisa la IA: sabés más de tu frasco que ella. */
      const nuevos = new Set(adivinados);
      const siguiente = { ...f };
      for (const campo of ["marca", "concentracion", "familia", "momento"]) {
        if (!siguiente[campo] && propuesta[campo]) {
          siguiente[campo] = propuesta[campo];
          nuevos.add(campo);
        }
      }
      setF(siguiente);
      setAdivinados(nuevos);
      setAviso(
        nuevos.size
          ? t("perfumes.iaPropuso", "La IA completó los campos marcados. Revísalos antes de guardar.")
          : t("perfumes.iaNadaNuevo", "La IA no tenía nada que agregar a lo que ya escribiste.")
      );
    } catch (e) {
      setError(tError(e));
    } finally {
      setConsultando(false);
    }
  }

  async function guardar(e) {
    e.preventDefault();
    if (!f.nombre.trim()) {
      setError(t("perfumes.faltaNombre", "Escribe primero el nombre."));
      return;
    }
    setGuardando(true);
    setError("");
    const cuerpo = {
      nombre: f.nombre.trim(),
      marca: f.marca.trim() || null,
      concentracion: f.concentracion || null,
      familia: f.familia || null,
      momento: f.momento || null,
      ml: f.ml === "" ? null : Number(f.ml),
      nivel: Number(f.nivel),
      abierto_en: f.abierto_en || null,
    };
    try {
      const r = inicial ? await api.editarPerfume({ id: inicial.id, ...cuerpo }) : await api.crearPerfume(cuerpo);
      onGuardado(r.perfume);
    } catch (e2) {
      setError(tError(e2));
    } finally {
      setGuardando(false);
    }
  }

  const select = (campo, lista, nombre, etiqueta) => (
    <label className="campo-mini">
      <span>{etiqueta}</span>
      <select className={marca(campo)} value={f[campo]} onChange={(e) => poner(campo, e.target.value)}>
        <option value="">{t("perfumes.sinDato", "Sin dato")}</option>
        {lista.map((v) => (
          <option key={v} value={v}>
            {nombre(v)}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <form className="editor perfume-form" onSubmit={guardar}>
      <p className="prenda-nombre">
        {inicial ? t("perfumes.editando", "Editar perfume") : t("perfumes.nuevo", "Nuevo perfume")}
      </p>

      <div className="ficha-fila">
        <label className="campo-mini campo-ancho">
          <span>{t("perfumes.nombre", "Nombre")}</span>
          <input
            value={f.nombre}
            onChange={(e) => poner("nombre", e.target.value)}
            maxLength={120}
            placeholder={t("perfumes.nombrePista", "Por ejemplo: Sauvage")}
            required
          />
        </label>
        <label className="campo-mini">
          <span>{t("perfumes.marca", "Marca")}</span>
          <input className={marca("marca")} value={f.marca} onChange={(e) => poner("marca", e.target.value)} maxLength={80} />
        </label>
      </div>

      <div>
        <button type="button" className="boton-secundario" onClick={completarConIA} disabled={consultando || guardando}>
          {consultando ? t("perfumes.completando", "Consultando...") : t("perfumes.completarIA", "Completar con IA")}
        </button>
      </div>
      {aviso && (
        <p className="texto-mini tenue" role="status">
          {aviso}
        </p>
      )}

      <div className="ficha-fila">
        {select("concentracion", CONCENTRACIONES, nombreConcentracion, t("perfumes.etiquetaConcentracion", "Concentración"))}
        {select("familia", FAMILIAS, nombreFamilia, t("perfumes.etiquetaFamilia", "Familia olfativa"))}
        {select("momento", MOMENTOS, nombreMomento, t("perfumes.etiquetaMomento", "Momento del día"))}
      </div>

      <div className="ficha-fila">
        <label className="campo-mini">
          <span>{t("perfumes.ml", "Tamaño del frasco (ml)")}</span>
          <input type="number" inputMode="numeric" min="1" max="1000" value={f.ml} onChange={(e) => poner("ml", e.target.value)} />
        </label>
        <label className="campo-mini">
          <span>{t("perfumes.abiertoEn", "Fecha de apertura")}</span>
          <input type="date" max={hoyLocal()} value={f.abierto_en} onChange={(e) => poner("abierto_en", e.target.value)} />
        </label>
      </div>

      <label className="perfume-nivel">
        <span className="texto-mini">{t("perfumes.nivel", "Queda: {n}%", { n: f.nivel })}</span>
        <input
          type="range"
          min="0"
          max="100"
          step="5"
          value={f.nivel}
          onChange={(e) => poner("nivel", Number(e.target.value))}
          aria-label={t("perfumes.nivelEtiqueta", "Cuánto queda en el frasco")}
        />
      </label>

      {error && (
        <p className="mensaje-error texto-mini" role="alert">
          {error}
        </p>
      )}

      <div className="editor-acciones">
        <button type="submit" className="boton-primario" disabled={guardando}>
          {guardando ? t("perfumes.guardando", "Guardando...") : t("perfumes.guardar", "Guardar")}
        </button>
        <button type="button" className="boton-texto" onClick={onCancelar} disabled={guardando}>
          {t("perfumes.cancelar", "Cancelar")}
        </button>
      </div>
    </form>
  );
}
